/** Mirrors day-global-calculations' recalculate response (internal/recalculate/recalculate.go: Result, ShiftResult, DayResult); day-superadmin-server passes the body through untouched. */
export type PluginOutputs = Record<string, Record<string, unknown>>;

/** Provenance of the one plan that ran; its stages hold both grains, the day-grain plugins in the last ones. */
export interface PlanProvenance {
  /** Named template that overrode the assigned pay policy. Absent when the real policy ran. */
  policy?: string;
  versions: Record<string, string>;
  stages: string[][];
  autoLoaded: string[];
}

export interface ShiftRun {
  date: string;
  shiftId: string;
  payPolicyUuid?: string;
  outputs?: PluginOutputs;
  error?: string;
}

/**
 * One date's day-grain run: the outputs of the plugins that compute once per date over its shifts (working_days_in_row, it_daily_overtime, …).
 * The engine's `unchanged` flag is not mirrored, as on ShiftRun: this page always asks with `force: true`, and a forced run skips nothing.
 */
export interface DayRun {
  date: string;
  payPolicyUuid?: string;
  outputs?: PluginOutputs;
  error?: string;
}

/** One pay policy's resolution record for this run. `written: false` means an identical row already existed. */
export interface PayPolicySnapshot {
  payPolicyUuid: string;
  snapshotUuid: string;
  written: boolean;
}

export interface RecalculateResponse {
  content: {
    shifts: ShiftRun[];
    /** Absent when the policy roots no day-grain plugin. */
    days?: DayRun[];
  };
  metadata: PlanProvenance & {
    /** Assigned pay policy the plan came from. Absent when `policy` overrode it or no policy applied to the range. */
    payPolicy?: string;
    sources: Record<string, number>;
    snapshotsWritten: Record<string, number>;
    payPolicySnapshots: PayPolicySnapshot[];
    summariesWritten: { shifts: number; days: number };
  };
}

export interface DateRange {
  from: string;
  to: string;
}

export interface PunchRef {
  type: string;
  index: number;
  minutes: number;
}

export interface ExpectedRange {
  start: PunchRef;
  end: PunchRef;
  duration: number;
  [k: string]: unknown;
}

export interface ExpectedPunchesOutput {
  work?: ExpectedRange | null;
  breaks?: ExpectedRange[];
  additional?: ExpectedRange[];
  absences?: ExpectedRange[];
}

/** One range of the shared timeline shape emitted by expected_ranges (and, later, actual_ranges). */
export type TimelineRangeKind = 'work' | 'break' | 'absence' | 'additional_work';

export interface TimelineRange {
  kind: TimelineRangeKind;
  startMinutes: number;
  endMinutes: number;
  meta: Record<string, unknown>;
}

export interface ActualPunch {
  key: string;
  type: string;
  absoluteMinutes: number;
  punchUuid: string;
  requestUuid?: string;
  paid?: boolean;
  allDay?: boolean;
}

/** One un-projected request range for the shift's day, emitted by absence_ranges. */
export interface AbsenceRange {
  requestUuid: string;
  startMinutes: number;
  endMinutes: number;
  paid: boolean;
  allDay: boolean;
  hoursCalculationType: string;
}

/** The it_split_shifts metric for the shift's day: how many worked blocks long unpaid intervals broke it into, and whether that breaches the policy's cap. */
export interface SplitShifts {
  count: number;
  isBreached: boolean;
}

/** One request attributed to the shift's own day, emitted by attributed_requests (shift grain). Only the fields the Report shows, not every field the engine's sourcetypes.Request carries. */
export interface AttributedRequestRow {
  uuid: string;
  approvalStatus: string;
  requestType: string;
  requestSubtypeName: string | null;
  hoursCalculationType: string;
  startedAt: string;
  endedAt: string;
  timezone: string;
  allDay: boolean;
  paid: boolean;
}

/** The configured night period the night_ranges plugin split against, on the shift's minute axis (end past 1440 when it ends the next morning). */
export interface NightWindow {
  startMinutes: number;
  endMinutes: number;
  applyEntirePeriod: boolean;
}

/** One shift-relative interval as emitted by early_late_ranges' four lists. */
export interface MinutesRange {
  startMinutes: number;
  endMinutes: number;
}

/** One daily-overtime stretch within one tier, emitted by the day-grain it_daily_overtime plugin. */
export interface DailyOvertimeRange extends MinutesRange {
  tier: string;
  percent: number;
}

/**
 * The week the day falls in, as it_week_anchor resolved it (§0.1): naive local wall-clock instants
 * (`YYYY-MM-DDTHH:MM:SS`, no zone offset), `close` exclusive, so an hour worked exactly at `close`
 * belongs to the next week. Every day inside one week reports the same window.
 */
export interface WeekWindow {
  open: string;
  close: string;
}

/** Minutes of daily overtime in one configured tier, every tier present even at zero. */
export interface DailyOvertimeTierMinutes {
  tier: string;
  percent: number;
  minutes: number;
}

/** One weekly-overtime stretch emitted by the day-grain it_weekly_overtime plugin (Italian §1). `tier` is set only when `tag` is `weekly`; a `paid_as_daily` range carries no percent. */
export interface WeeklyOvertimeRange extends MinutesRange {
  tag: string;
  tier: string;
  percent: number;
}

/** Minutes of the day per weekly-overtime tag, in engine order: ordinary, contractual, one `weekly` row per tier (zero included), paid_as_daily, paid_as_weekly. `percent` is priced only on contractual and weekly rows. */
export interface WeeklyOvertimeTagMinutes {
  tag: string;
  tier: string;
  percent: number;
  minutes: number;
}

/** One open deposit of the hours bank (§11) as of this day, oldest first: the day it was earned, the day it expires, and what is left of it. */
export interface HoursBankDeposit {
  earnedOn: string;
  expiresOn: string;
  minutes: number;
}

/** One break type's minutes as emitted by the *_break_by_types plugins, in their `day` and `night` lists. */
export interface BreakTypeSummary {
  breakTypeUuid: string;
  minutes: number;
  breakMode?: string;
  isWorkingHours?: boolean;
  excludeFromMissingTime?: boolean;
}

export type PunchType = 'entry' | 'exit' | 'break_start' | 'break_end';

export interface ScheduleEvent {
  key: string;
  type: PunchType;
  time: number | null;
  duration?: number | null;
  breakMode?: 'duration' | 'range' | 'flexible';
  startAfter?: number;
}

export interface ScheduleShift {
  shiftId: string;
  working: boolean;
  isDsrDay: boolean;
  nonWorkingDayShift: boolean;
  events: ScheduleEvent[];
  /** Planned working minutes of a flexible shift (day-schedules passes this through). Absent/null for a fixed-time shift. */
  shiftDuration?: number | null;
  /** e.g. 'flexible'. Absent for a fixed-time shift. */
  scheduleType?: string;
}

export interface PunchItem {
  uuid: string;
  deviceDatetime: string;
  punchType: PunchType;
  status: string;
  isManual: boolean;
  source: string;
  shiftId?: string | null;
  shiftEventKey?: string | null;
}

export interface DayEntry {
  date: string;
  shifts: ScheduleShift[];
  punches: PunchItem[];
  /** Punches ignored on this date — dropped from `punches`, counted here instead. */
  ignoredCount: number;
}

export interface DayDataContext {
  timeZone: string;
  timeZoneSource: 'location' | 'company';
  defaultLocation?: { uuid: string; name: string };
  employee: { fullName: string; employeeStatus: string; active: boolean };
  adminRequester?: { uuid: string; fullName: string };
}

export interface DayDataResponse {
  context: DayDataContext;
  days: DayEntry[];
}

export interface PlannedPunch {
  date: string;
  time: string;
  punchType: PunchType;
  shiftId?: string;
  shiftEventKey?: string;
}

/** Server-side cap on punches per create-punches request, enforced client-side too so the dialogs can disable Create and explain why before submitting. */
export const MAX_PUNCHES_PER_REQUEST = 200;

/** Who the punch is created as. `employee` is a manual request (pending); `employee_realtime` is like a clock punch (approved, not manual); `admin` is approved. */
export type CreatedBy = 'employee' | 'employee_realtime' | 'admin';

export interface CreatePunchesRequest {
  createdBy: CreatedBy;
  requestedBy?: string;
  reason?: string;
  punches: PlannedPunch[];
}

export interface CreatePunchesResponse {
  created: { uuid: string; railsUuid: string; date: string; time: string; punchType: PunchType }[];
  failed: { date: string; time: string; punchType: PunchType; error: string; railsUuid?: string }[];
}

/** `declined` is day-punching's own success count, not the request size. `failed` lists
 * day-punching uuids it refused to decline; `railsSkipped` lists punches with no Rails mapping
 * (declined in day-punching only, not a failure); `railsFailed` lists punches whose Rails decline
 * call itself failed (still declined in day-punching). */
export interface DeclinePunchesResponse {
  declined: number;
  failed: string[];
  railsSkipped: string[];
  railsFailed: string[];
}
